import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const fetchAdminRequestedLeaveList = createAsyncThunk(
  'adminHr/fetchRequestedLeaveList',
  async ({ approversEmail }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/AdminHoliday/RequestedLeaveList`, {
        signal,
        headers: { 'Content-Type': 'application/json' },
        params: { ApproversEmail: approversEmail },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/fetchRequestedLeaveList failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch requested leave list');
    }
  }
);

export const fetchAdminHolidayOverviewRows = createAsyncThunk(
  'adminHr/fetchHolidayOverviewRows',
  async (_, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/Admin/Leave/HolidayOverviewRows`, {
        signal,
        headers: { 'Content-Type': 'application/json' },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/fetchHolidayOverviewRows failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch holiday overview rows');
    }
  }
);

export const fetchAdminCalendarData = createAsyncThunk(
  'adminHr/fetchCalendarData',
  async (_, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/CalendarData`, {
        signal,
        headers: { 'Content-Type': 'application/json' },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/fetchCalendarData failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch calendar data');
    }
  }
);

export const fetchAdminCalendarDataWithParams = createAsyncThunk(
  'adminHr/fetchCalendarDataWithParams',
  async ({ params }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/CalendarData`, {
        signal,
        headers: { 'Content-Type': 'application/json' },
        params,
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/fetchCalendarDataWithParams failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch calendar data');
    }
  }
);

export const approveLeaveRequest = createAsyncThunk(
  'adminHr/approveLeaveRequest',
  async ({ leaveId, approversEmail }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/AdminHoliday/ApprovingLeaveRequest`,
        { leaveID: leaveId, ApproversEmail: approversEmail },
        { signal, headers: { 'Content-Type': 'application/json' } }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/approveLeaveRequest failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to approve leave request');
    }
  }
);

export const rejectLeaveRequest = createAsyncThunk(
  'adminHr/rejectLeaveRequest',
  async ({ leaveId, approversEmail, comment }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/AdminHoliday/RejectingLeaveRequest`,
        { leaveID: leaveId, ApproversEmail: approversEmail, comment },
        { signal, headers: { 'Content-Type': 'application/json' } }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/rejectLeaveRequest failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to reject leave request');
    }
  }
);

export const onboardEmployee = createAsyncThunk(
  'adminHr/onboardEmployee',
  async ({ formData }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(`${API_URL}/api/employee/onboard`, formData, {
        signal,
        headers: { 'Content-Type': 'application/json' },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/onboardEmployee failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to onboard employee');
    }
  }
);

export const fetchAdminSicknessPageData = createAsyncThunk(
  'adminHr/fetchSicknessPageData',
  async ({ userEmail }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/Admin/Sickness/PageData`, {
        signal,
        headers: { 'Content-Type': 'application/json' },
        params: { userEmail },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/fetchSicknessPageData failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch sickness page data');
    }
  }
);

export const createAdminSickness = createAsyncThunk(
  'adminHr/createSickness',
  async ({ userEmail, payload }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(`${API_URL}/api/Admin/Sickness/NewSickness`, payload, {
        signal,
        headers: { 'Content-Type': 'application/json' },
        params: { userEmail },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/createSickness failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to create sickness');
    }
  }
);

export const fetchAdminSicknessFormData = createAsyncThunk(
  'adminHr/fetchSicknessFormData',
  async ({ userEmail, leaveId }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/Admin/Sickness/FormData`, {
        signal,
        headers: { 'Content-Type': 'application/json' },
        params: { userEmail, LeaveID: leaveId },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/fetchSicknessFormData failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch sickness form');
    }
  }
);

export const signOffAdminSickness = createAsyncThunk(
  'adminHr/signOffSickness',
  async ({ userEmail, leaveId, adminComment }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/Admin/Sickness/SignOff`,
        { AdminComment: adminComment },
        {
          signal,
          headers: { 'Content-Type': 'application/json' },
          params: { userEmail, LeaveID: leaveId },
        }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/signOffSickness failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to sign off sickness');
    }
  }
);

export const returnEmployeeFromSickness = createAsyncThunk(
  'adminHr/returnEmployeeFromSickness',
  async ({ userEmail, returnedId, payload }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/Admin/Sickness/ReturningEmployee`,
        payload,
        {
          signal,
          headers: { 'Content-Type': 'application/json' },
          params: { userEmail, returnedId },
        }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('adminHr/returnEmployeeFromSickness failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to return employee');
    }
  }
);

const slice = createSlice({
  name: 'adminHr',
  initialState: { error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('adminHr/') && action.type.endsWith('/rejected'),
      (state, action) => {
        if (action?.meta?.aborted) return;
        state.error = action.payload || action.error?.message || null;
      }
    );
  },
});

export default slice.reducer;


